import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import '../css/AddUnit.css';

const AddCategory = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [categoryName, setCategoryName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const API_URL = process.env.REACT_APP_API_URL;
  
  const isEditMode = !!id;

  useEffect(() => {
    if (isEditMode) {
      // If we have category data in location state, use it
      if (location.state?.category) {
        setCategoryName(location.state.category.categoryname);
      } else {
        // Otherwise, fetch the category data
        fetchCategoryData();
      }
    }
  }, [id, isEditMode, location.state]);

  const fetchCategoryData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}get-category.php?id=${id}`);
      const data = await response.json();
      
      if (data.status && data.category) {
        setCategoryName(data.category.categoryname);
      } else {
        showToast('Failed to load category data', 'error');
        navigate('/category-list');
      }
    } catch (error) {
      showToast('Error loading category data', 'error');
      navigate('/category-list');
    } finally {
      setIsLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!categoryName.trim()) {
      showToast('Please enter a category name', 'error');
      return;
    }

    setIsSubmitting(true);

    let response;
    let data;
    try {
      const url = isEditMode ? `${API_URL}edit-category.php` : `${API_URL}add-category.php`;
      const body = isEditMode 
        ? JSON.stringify({ 
            category_id: parseInt(id), 
            categoryname: categoryName.trim() 
          })
        : JSON.stringify({ categoryname: categoryName.trim() });

      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: body,
      });
    } catch (fetchError) {
      showToast('Network error: ' + fetchError.message, 'error');
      setIsSubmitting(false);
      return;
    }

    try {
      data = await response.json();
    } catch (jsonError) {
      showToast('Invalid server response.', 'error');
      setIsSubmitting(false);
      return;
    }

    if (data.status) {
      const action = isEditMode ? 'updated' : 'added';
      showToast(`Category "${categoryName}" ${action} successfully!`, 'success');
      
      if (!isEditMode) {
        setCategoryName('');
      }
      
      setTimeout(() => {
        navigate('/category-list');
      }, 1500);
    } else {
      showToast(data.message || `Error ${isEditMode ? 'updating' : 'adding'} category. Please try again.`, 'error');
    }
    setIsSubmitting(false);
  };

  if (isLoading) {
    return (
      <div className="add-category-content">
        <div className="page-header" style={{display: 'block'}}>
          <h1>Loading...</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="add-category-content">
      <div className="page-header" style={{display: 'block'}}>
        <h1>{isEditMode ? 'Edit Category' : 'Add Category'}</h1>
        <p>{isEditMode ? 'Update category information' : 'Create a new category for your menu'}</p>
      </div>
      <div className="form-container">
        <form onSubmit={handleSubmit} className="add-unit-form">
          <div className="form-group">
            <label htmlFor="categoryName">Category Name</label>
            <input
              type="text"
              id="categoryName"
              name="categoryName"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="Enter category name (e.g., Starters, Desserts)"
              required
              disabled={isSubmitting}
            />
          </div>
          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting 
              ? (isEditMode ? 'Updating Category...' : 'Adding Category...') 
              : (isEditMode ? 'Update Category' : 'Add Category')
            }
          </button>
        </form>
      </div>
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close" 
              onClick={() => setToast({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddCategory; 